/* ===== Admin Service Functions ===== */
import { $ } from '../utils/helpers.js?v=83.58';
import { showSnackbar } from '../utils/ui.js?v=83.58';
import { db } from '../config/firebase.js?v=83.58';
import { storage } from '../config/firebase.js?v=83.58';
import { getCurrentUser } from './auth.js?v=83.58';
import {
  getCurrentSettings,
  setCurrentSettings,
  loadSettings,
  persistClientTemplateChoice,
  saveAdminSettings,
  saveLocationHoursSettings,
  saveReservationSettings,
  saveLocationMapSettings,
  saveLocationLogoSettings,
  deleteLocationLogo,
  saveEventModuleSettings
} from './settings.js?v=83.58';
import { saveClientInterfaceModuleSettings } from './client-interface-module-save.js?v=83.58';
import { saveCampaignSettingsBackend } from './backend.js?v=83.58';
import { resetClaimsUnsub } from './claims.js?v=83.58';
import { resetAdminUI } from '../ui/admin.js?v=83.58';
import { resetRegisterForm } from '../ui/forms.js?v=83.58';
import { showPage } from '../ui/routing.js?v=83.58';
import { auth } from '../config/firebase.js?v=83.58';
import { logger } from '../utils/logger.js?v=83.58';
import { withSaveButtonState } from '../utils/save-button-guard.js?v=83.58';
import { loadScriptOnce } from '../utils/lazy-loader.js?v=83.58';
import { showLoadingOverlay, hideLoadingOverlay } from '../utils/loading.js?v=83.58';
import { 
  startCampaignBackend, 
  stopCampaignBackend, 
  resetTriesBackend, 
  exportUserDataBackend, 
  deleteUserAccountBackend,
  suggestClientLayoutAIBackend
} from './backend.js?v=83.58';
import { applyExternalLayoutProposal, getClientLayoutConfig } from '../ui/client-layout-renderer.js?v=83.58';
import {
  CLIENT_LAYOUT_ALLOWED_SIZES,
  CLIENT_LAYOUT_ALLOWED_STYLES,
  CLIENT_LAYOUT_BLOCKS,
  DEFAULT_CLIENT_LAYOUT_CONFIG,
  applyHeroTopSectionToOrder,
  deriveHeroTopSection
} from '../ui/client-layout-config.js?v=83.58';
import {
  CLIENT_TEMPLATE_LABELS,
  applyClientTemplateToPage,
  getClientTemplateStyleFromSettings,
  restoreAiPreviewToStandardVisual,
  syncClientTemplateHeader
} from '../ui/client-template-style.js?v=83.58';

// Flag pentru prevenirea click-urilor multiple
let isSaving = false;
let isStarting = false;
let isStopping = false;
let aiLayoutPendingProposal = null;
let aiSettingsPatchPending = null;
let aiLayoutGenerating = false;
let aiLayoutApplying = false;
let aiLayoutBaseConfig = null;
let aiLiveEditBound = false;
let aiLiveControlsRepositionBound = false;

const AI_BLOCK_LABELS = {
  menu: 'Menu',
  review: 'Review',
  social: 'Social',
  promo: 'Promo',
  campaign: 'Campaign',
  rules: 'Rules'
};
const AI_SIZE_ORDER = ['sm', 'md', 'lg', 'xl'];
const AI_DRAGGABLE_BLOCKS = ['menu', 'review', 'social', 'promo'];
const AI_HERO_SECTIONS = ['promo', 'duo'];
const AI_HERO_SECTION_LABELS = { promo: 'Oferte', duo: 'Meniu + Recenzie' };
let aiTemplateBaseStyle = 'modern';
let aiTemplateBaseAiVariant = null;
let aiTemplatePendingStyle = 'modern';
let aiThemeStripOpen = false;

function syncAiTemplatePendingStyleWindow() {
  if (typeof window !== 'undefined') {
    window.__aiTemplatePendingStyle = aiTemplatePendingStyle;
  }
}
let aiTemplatePendingAiVariant = null;
let lastAiTemplatePick = null;
const aiLiveOffsets = { campaignX: 0, rulesX: 0 };

async function refreshClientPreviewUi() {
  const { applyClientUI, schedulePromoIsolation } = await import('../ui/slideshow.js?v=83.58');
  applyClientUI();
  try {
    schedulePromoIsolation?.(getCurrentSettings() || {});
  } catch (_) {
    /* ignore */
  }
}

function cloneDefaultClientLayoutConfig() {
  return JSON.parse(JSON.stringify(DEFAULT_CLIENT_LAYOUT_CONFIG));
}

function applyStandardLayoutDefaults() {
  const defaultLayout = cloneDefaultClientLayoutConfig();
  aiLayoutPendingProposal = null;
  const settings = getCurrentSettings() || {};
  setCurrentSettings({ ...settings, clientLayoutConfig: defaultLayout });
  applyExternalLayoutProposal(defaultLayout);
}

function applyAiGeneratedBundle(response, variant) {
  aiTemplatePendingStyle = 'ai-generated';
  syncAiTemplatePendingStyleWindow();
  aiTemplatePendingAiVariant = variant;
  applyClientTemplateToPage('ai-generated', { aiVariant: variant });

  if (response?.proposal && typeof response.proposal === 'object') {
    aiLayoutPendingProposal = response.proposal;
    applyExternalLayoutProposal(response.proposal);
    hydrateAiLiveOffsetsFromProposal();
    applyAiLiveOffsets();
  }

  const patch = response?.settingsPatch && typeof response.settingsPatch === 'object'
    ? { ...response.settingsPatch }
    : {};
  if (response?.buttonColor && !patch.buttonColor) {
    patch.buttonColor = response.buttonColor;
  }
  if (Object.keys(patch).length > 0) {
    aiSettingsPatchPending = patch;
    applyAiSettingsPatchToForm(patch);
    const settings = getCurrentSettings() || {};
    setCurrentSettings({ ...settings, ...patch });
  }
}

function hydrateAiLiveOffsetsFromProposal() {
  const campaignX = Number(aiLayoutPendingProposal?.offsetX?.campaign || 0);
  const rulesX = Number(aiLayoutPendingProposal?.offsetX?.rules || 0);
  aiLiveOffsets.campaignX = Number.isFinite(campaignX) ? Math.max(-160, Math.min(160, campaignX)) : 0;
  aiLiveOffsets.rulesX = Number.isFinite(rulesX) ? Math.max(-160, Math.min(160, rulesX)) : 0;
}

function syncAiLiveOffsetsIntoProposal() {
  if (!aiLayoutPendingProposal || typeof aiLayoutPendingProposal !== 'object') return;
  if (!aiLayoutPendingProposal.offsetX || typeof aiLayoutPendingProposal.offsetX !== 'object') {
    aiLayoutPendingProposal.offsetX = {};
  }
  aiLayoutPendingProposal.offsetX.campaign = Math.round(Math.max(-160, Math.min(160, aiLiveOffsets.campaignX || 0)));
  aiLayoutPendingProposal.offsetX.rules = Math.round(Math.max(-160, Math.min(160, aiLiveOffsets.rulesX || 0)));
}

function applyAiSettingsPatchToForm(patch) {
  if (!patch || typeof patch !== 'object') return;
  const normalizeText = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  const setValue = (id, value) => {
    const el = $(id);
    if (!el) return;
    el.value = String(value);
  };
  if (patch.slideDuration !== undefined) setValue('slideDuration', patch.slideDuration);
  if (patch.totalTries !== undefined) setValue('totalTries', patch.totalTries);
  if (patch.startDate !== undefined) setValue('campStart', patch.startDate);
  if (patch.endDate !== undefined) setValue('campEnd', patch.endDate);
  if (patch.clientPromoGalleryTitle !== undefined) {
    setValue('clientPromoGalleryTitle', patch.clientPromoGalleryTitle);
  }
  if (patch.buttonColor !== undefined) {
    setValue('buttonColor', patch.buttonColor);
  }
  if (Array.isArray(patch.prizeCommands) && patch.prizeCommands.length > 0) {
    const cards = Array.from(document.querySelectorAll('#prizeContainer .prize-card'));
    patch.prizeCommands.forEach((cmd) => {
      if (!cmd || typeof cmd !== 'object') return;
      if (cmd.action === 'set_all_count' && Number.isFinite(Number(cmd.count))) {
        const n = String(Math.max(0, Math.round(Number(cmd.count))));
        cards.forEach((node) => {
          const input = node.querySelector('.prizeCount');
          if (input) input.value = n;
        });
        return;
      }
      const target = normalizeText(cmd.targetName || '');
      if (!target) return;
      const card = cards.find((node) => {
        const msg = normalizeText(node.querySelector('.prizeMsg')?.value || '');
        return msg.includes(target) || target.includes(msg);
      });
      if (!card) return;
      if (cmd.action === 'set_count' && Number.isFinite(Number(cmd.count))) {
        const input = card.querySelector('.prizeCount');
        if (input) input.value = String(Math.max(0, Math.round(Number(cmd.count))));
      }
      if (cmd.action === 'deactivate') {
        const input = card.querySelector('.prizeCount');
        if (input) input.value = '0';
      }
      if (cmd.action === 'rename') {
        const input = card.querySelector('.prizeMsg');
        if (input && String(cmd.newName || '').trim()) input.value = String(cmd.newName).trim();
      }
    });
  }
}

function formatAiOrder(order = []) {
  return order.map((id) => AI_BLOCK_LABELS[id] || id).join(' -> ');
}

function formatAiDiffLines(currentConfig, proposalConfig) {
  const lines = [];
  const curOrder = Array.isArray(currentConfig?.order) ? currentConfig.order : [];
  const nextOrder = Array.isArray(proposalConfig?.order) ? proposalConfig.order : [];
  if (JSON.stringify(curOrder) !== JSON.stringify(nextOrder)) {
    lines.push(`Ordine: ${formatAiOrder(nextOrder)}`);
  }
  const curHero = currentConfig?.heroTopSection || deriveHeroTopSection(curOrder);
  const nextHero = proposalConfig?.heroTopSection || deriveHeroTopSection(nextOrder);
  if (curHero !== nextHero) {
    lines.push(
      `Secțiune sus: ${AI_HERO_SECTION_LABELS[curHero] || curHero} -> ${AI_HERO_SECTION_LABELS[nextHero] || nextHero}`
    );
  }

  const ids = ['menu', 'review', 'social', 'promo', 'campaign', 'rules'];
  ids.forEach((id) => {
    const cSize = currentConfig?.sizes?.[id] || 'md';
    const nSize = proposalConfig?.sizes?.[id] || 'md';
    const cStyle = currentConfig?.style?.[id] || 'glass';
    const nStyle = proposalConfig?.style?.[id] || 'glass';
    const cHi = currentConfig?.highlight?.[id] === true;
    const nHi = proposalConfig?.highlight?.[id] === true;
    const cVis = currentConfig?.visible?.[id] !== false;
    const nVis = proposalConfig?.visible?.[id] !== false;
    const changes = [];
    if (cSize !== nSize) changes.push(`size ${cSize} -> ${nSize}`);
    if (cStyle !== nStyle) changes.push(`style ${cStyle} -> ${nStyle}`);
    if (cHi !== nHi) changes.push(`highlight ${cHi ? 'on' : 'off'} -> ${nHi ? 'on' : 'off'}`);
    if (cVis !== nVis) changes.push(`visible ${cVis ? 'on' : 'off'} -> ${nVis ? 'on' : 'off'}`);
    if (changes.length > 0) {
      lines.push(`${AI_BLOCK_LABELS[id] || id}: ${changes.join(', ')}`);
    }
  });

  return lines.length > 0 ? lines : ['Nu există diferențe semnificative față de layout-ul curent.'];
}

function renderAiLayoutChanges(currentConfig, proposalConfig) {
  const host = $('aiLayoutChangesList');
  const wrap = $('aiLayoutChangesWrap');
  if (!host || !wrap) return;

  const lines = formatAiDiffLines(currentConfig, proposalConfig);
  host.replaceChildren();
  lines.forEach((line) => {
    const row = document.createElement('div');
    row.textContent = `- ${line}`;
    host.appendChild(row);
  });
  wrap.style.display = '';
}

function resetAiLayoutChanges() {
  const host = $('aiLayoutChangesList');
  const wrap = $('aiLayoutChangesWrap');
  if (host) host.replaceChildren();
  if (wrap) wrap.style.display = 'none';
}

function resetAiManualEditor() {
  const host = $('aiLayoutManualEditorList');
  const wrap = $('aiLayoutManualEditorWrap');
  if (host) host.replaceChildren();
  if (wrap) wrap.style.display = 'none';
}

function appendAiChatLine(text) {
  const box = $('aiLayoutChatMessages');
  if (!box) return;
  const line = document.createElement('div');
  line.textContent = text;
  box.appendChild(line);
  box.scrollTop = box.scrollHeight;
}

function renderAiManualEditor() {
  const host = $('aiLayoutManualEditorList');
  const wrap = $('aiLayoutManualEditorWrap');
  if (!host || !wrap || !aiLayoutPendingProposal) return;
  host.replaceChildren();

  const cfg = aiLayoutPendingProposal;
  const order = Array.isArray(cfg.order) ? [...cfg.order] : [...CLIENT_LAYOUT_BLOCKS];
  const reorderBlocks = (dragId, targetId, before) => {
    if (!dragId || !targetId || dragId === targetId) return;
    const arr = [...aiLayoutPendingProposal.order];
    const from = arr.indexOf(dragId);
    const to = arr.indexOf(targetId);
    if (from < 0 || to < 0) return;
    arr.splice(from, 1);
    const insertAt = before ? (from < to ? to - 1 : to) : (from < to ? to : to + 1);
    arr.splice(Math.max(0, Math.min(arr.length, insertAt)), 0, dragId);
    aiLayoutPendingProposal.order = arr;
  };
  const rerender = () => {
    syncAiProposalHeroFromOrder();
    applyExternalLayoutProposal(aiLayoutPendingProposal);
    renderAiLayoutChanges(aiLayoutBaseConfig || getClientLayoutConfig(getCurrentSettings() || {}), aiLayoutPendingProposal);
    renderAiManualEditor();
  };

  const heroRow = document.createElement('div');
  heroRow.className = 'client-layout-editor-item';
  heroRow.style.flexWrap = 'wrap';
  const heroLabel = document.createElement('div');
  heroLabel.className = 'client-layout-editor-item__name';
  heroLabel.textContent = 'Secțiune sus';
  const heroActions = document.createElement('div');
  heroActions.className = 'client-layout-editor-item__actions';
  const promoTopBtn = document.createElement('button');
  promoTopBtn.type = 'button';
  promoTopBtn.className = 'btn-ghost';
  promoTopBtn.textContent = 'Oferte';
  promoTopBtn.disabled = (cfg.heroTopSection || deriveHeroTopSection(cfg.order)) === 'promo';
  promoTopBtn.addEventListener('click', () => {
    applyAiHeroTopSection('promo');
    renderAiManualEditor();
  });
  const duoTopBtn = document.createElement('button');
  duoTopBtn.type = 'button';
  duoTopBtn.className = 'btn-ghost';
  duoTopBtn.textContent = 'Meniu+Recenzie';
  duoTopBtn.disabled = (cfg.heroTopSection || deriveHeroTopSection(cfg.order)) === 'duo';
  duoTopBtn.addEventListener('click', () => {
    applyAiHeroTopSection('duo');
    renderAiManualEditor();
  });
  heroActions.appendChild(promoTopBtn);
  heroActions.appendChild(duoTopBtn);
  heroRow.appendChild(heroLabel);
  heroRow.appendChild(heroActions);
  host.appendChild(heroRow);

  order.forEach((blockId, idx) => {
    const row = document.createElement('div');
    row.className = 'client-layout-editor-item';
    row.dataset.blockId = blockId;

    const name = document.createElement('div');
    name.className = 'client-layout-editor-item__name';
    name.textContent = AI_BLOCK_LABELS[blockId] || blockId;

    const actions = document.createElement('div');
    actions.className = 'client-layout-editor-item__actions';

    const dragHandle = document.createElement('button');
    dragHandle.type = 'button';
    dragHandle.className = 'btn-ghost client-layout-editor-item__drag';
    dragHandle.textContent = '↕';
    dragHandle.title = 'Trage pentru reordonare';
    dragHandle.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      const dragId = blockId;
      row.classList.add('is-dragging');
      let currentTarget = null;
      let before = true;

      const clearDropHints = () => {
        host.querySelectorAll('.client-layout-editor-item.ai-drop-before, .client-layout-editor-item.ai-drop-after').forEach((el) => {
          el.classList.remove('ai-drop-before', 'ai-drop-after');
        });
      };

      const onMove = (e) => {
        const el = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('.client-layout-editor-item[data-block-id]');
        clearDropHints();
        if (!el || el.dataset.blockId === dragId) {
          currentTarget = null;
          return;
        }
        currentTarget = el.dataset.blockId;
        const rect = el.getBoundingClientRect();
        before = e.clientY < (rect.top + rect.height / 2);
        el.classList.add(before ? 'ai-drop-before' : 'ai-drop-after');
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        clearDropHints();
        row.classList.remove('is-dragging');
        if (currentTarget) {
          reorderBlocks(dragId, currentTarget, before);
          rerender();
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });

    const moveUp = document.createElement('button');
    moveUp.type = 'button';
    moveUp.className = 'btn-ghost';
    moveUp.textContent = '↑';
    moveUp.disabled = idx === 0;
    moveUp.addEventListener('click', () => {
      const i = aiLayoutPendingProposal.order.indexOf(blockId);
      if (i <= 0) return;
      const x = aiLayoutPendingProposal.order[i - 1];
      aiLayoutPendingProposal.order[i - 1] = aiLayoutPendingProposal.order[i];
      aiLayoutPendingProposal.order[i] = x;
      rerender();
    });

    const moveDown = document.createElement('button');
    moveDown.type = 'button';
    moveDown.className = 'btn-ghost';
    moveDown.textContent = '↓';
    moveDown.disabled = idx >= order.length - 1;
    moveDown.addEventListener('click', () => {
      const i = aiLayoutPendingProposal.order.indexOf(blockId);
      if (i < 0 || i >= aiLayoutPendingProposal.order.length - 1) return;
      const x = aiLayoutPendingProposal.order[i + 1];
      aiLayoutPendingProposal.order[i + 1] = aiLayoutPendingProposal.order[i];
      aiLayoutPendingProposal.order[i] = x;
      rerender();
    });

    const sizeSel = document.createElement('select');
    sizeSel.className = 'client-layout-editor-item__select';
    CLIENT_LAYOUT_ALLOWED_SIZES.forEach((sz) => {
      const opt = document.createElement('option');
      opt.value = sz;
      opt.textContent = sz;
      if ((cfg.sizes?.[blockId] || 'md') === sz) opt.selected = true;
      sizeSel.appendChild(opt);
    });
    sizeSel.addEventListener('change', () => {
      aiLayoutPendingProposal.sizes[blockId] = sizeSel.value;
      rerender();
    });

    const styleSel = document.createElement('select');
    styleSel.className = 'client-layout-editor-item__select';
    CLIENT_LAYOUT_ALLOWED_STYLES.forEach((st) => {
      const opt = document.createElement('option');
      opt.value = st;
      opt.textContent = st;
      if ((cfg.style?.[blockId] || 'glass') === st) opt.selected = true;
      styleSel.appendChild(opt);
    });
    styleSel.addEventListener('change', () => {
      aiLayoutPendingProposal.style[blockId] = styleSel.value;
      rerender();
    });

    const hiWrap = document.createElement('label');
    hiWrap.className = 'client-layout-editor-item__toggle';
    const hiChk = document.createElement('input');
    hiChk.type = 'checkbox';
    hiChk.checked = cfg.highlight?.[blockId] === true;
    hiChk.addEventListener('change', () => {
      aiLayoutPendingProposal.highlight[blockId] = hiChk.checked;
      rerender();
    });
    const hiTxt = document.createElement('span');
    hiTxt.textContent = 'highlight';
    hiWrap.appendChild(hiChk);
    hiWrap.appendChild(hiTxt);

    actions.appendChild(dragHandle);
    actions.appendChild(moveUp);
    actions.appendChild(moveDown);
    actions.appendChild(sizeSel);
    actions.appendChild(styleSel);
    actions.appendChild(hiWrap);
    row.appendChild(name);
    row.appendChild(actions);
    host.appendChild(row);
  });

  wrap.style.display = '';
}

function setAiClientPreviewMode(enabled) {
  if (typeof window !== 'undefined') {
    window.__aiLayoutPreviewActive = enabled === true;
    if (!enabled) {
      delete window.__aiTemplatePendingStyle;
    } else {
      syncAiTemplatePendingStyleWindow();
    }
  }
  const playBtn = document.getElementById('playBtn');
  const clientPage = document.getElementById('clientPage');
  if (clientPage) {
    clientPage.classList.toggle('ai-live-edit-active', enabled === true);
  }
  if (playBtn) {
    playBtn.disabled = enabled === true;
    playBtn.style.pointerEvents = enabled === true ? 'none' : '';
    playBtn.style.opacity = enabled === true ? '0.55' : '';
  }
  if (enabled === true && document.getElementById('clientPage')?.classList.contains('template-custom')) {
    import('../ui/client-custom-template.js?v=83.58')
      .then((custom) => {
        if (playBtn) playBtn.style.opacity = '';
        custom?.hideCustomIntruders?.();
      })
      .catch(() => {});
  }
  if (enabled !== true) {
    aiLiveOffsets.campaignX = 0;
    aiLiveOffsets.rulesX = 0;
    document.documentElement.style.removeProperty('--ai-campaign-offset-x');
    document.documentElement.style.removeProperty('--ai-rules-offset-x');
    document.querySelectorAll('.ai-live-block-controls').forEach((el) => el.remove());
    const bar = document.getElementById('aiLiveEditToolbar');
    if (bar) bar.remove();
    closeAiLayoutModelPicker();
    const savedSettings = getCurrentSettings() || {};
    const savedStyle = getClientTemplateStyleFromSettings(savedSettings);
    applyClientTemplateToPage(savedStyle, {
      aiVariant: savedSettings.clientTemplateAiVariant || null,
      settings: savedSettings
    });
    syncClientTemplateHeader(savedSettings);
    document.querySelectorAll('.ai-live-drop-before, .ai-live-drop-after').forEach((el) => {
      el.classList.remove('ai-live-drop-before', 'ai-live-drop-after');
    });
  }
  import('../ui/client-custom-template.js?v=83.58')
    .then((m) => m.syncCustomAccentFloatCard?.(getCurrentSettings() || {}))
    .catch(() => {});
}

function applyAiLiveOffsets() {
  document.documentElement.style.setProperty('--ai-campaign-offset-x', `${Math.round(aiLiveOffsets.campaignX)}px`);
  document.documentElement.style.setProperty('--ai-rules-offset-x', `${Math.round(aiLiveOffsets.rulesX)}px`);
}

function syncAiProposalHeroFromOrder() {
  if (!aiLayoutPendingProposal) return;
  const top = deriveHeroTopSection(aiLayoutPendingProposal.order);
  aiLayoutPendingProposal.heroTopSection = top;
  aiLayoutPendingProposal.order = applyHeroTopSectionToOrder(aiLayoutPendingProposal.order, top);
}

function applyAiHeroTopSection(heroTopSection) {
  if (!aiLayoutPendingProposal) return;
  const top = heroTopSection === 'duo' ? 'duo' : 'promo';
  aiLayoutPendingProposal.heroTopSection = top;
  aiLayoutPendingProposal.order = applyHeroTopSectionToOrder(aiLayoutPendingProposal.order, top);
  applyExternalLayoutProposal(aiLayoutPendingProposal);
  renderAiLayoutChanges(
    aiLayoutBaseConfig || getClientLayoutConfig(getCurrentSettings() || {}),
    aiLayoutPendingProposal
  );
  renderAiLiveBlockControls();
}

function adjustAiBlockSize(blockId, delta) {
  if (!aiLayoutPendingProposal) return;
  const current = aiLayoutPendingProposal?.sizes?.[blockId] || 'md';
  const idx = Math.max(0, AI_SIZE_ORDER.indexOf(current));
  const nextIdx = Math.max(0, Math.min(AI_SIZE_ORDER.length - 1, idx + delta));
  aiLayoutPendingProposal.sizes[blockId] = AI_SIZE_ORDER[nextIdx];
  applyExternalLayoutProposal(aiLayoutPendingProposal);
}

function renderAiLiveToolbar() {
  const existing = document.getElementById('aiLiveEditToolbar');
  if (existing) existing.remove();

  const bar = document.createElement('div');
  bar.id = 'aiLiveEditToolbar';
  bar.className = 'ai-live-edit-toolbar';

  const backBtn = document.createElement('button');
  backBtn.type = 'button';
  backBtn.className = 'btn-ghost ai-live-edit-toolbar__btn';
  backBtn.textContent = 'Admin';
  backBtn.title = 'Înapoi în Admin';
  backBtn.setAttribute('aria-label', 'Înapoi în Admin');
  backBtn.addEventListener('click', () => {
    exitAiLayoutPreview();
    showPage('adminPage');
  });

  const regenerateBtn = document.createElement('button');
  regenerateBtn.type = 'button';
  regenerateBtn.className = 'btn-ghost ai-live-edit-toolbar__btn';
  regenerateBtn.id = 'aiLayoutRegenerateBtn';
  regenerateBtn.textContent = 'Teme';
  regenerateBtn.title = 'Teme';
  regenerateBtn.setAttribute('aria-label', 'Teme');
  regenerateBtn.setAttribute('aria-expanded', 'false');

  // „Teme”: o bandă îngustă, într-un singur rând, lângă bară — doar numele temelor, pagina rămâne vizibilă
  const THEMES = [
    ['cloud', 'Cloud Dark', '#14161A'], ['cloud-light', 'Cloud Luminos', '#F3EFE6'],
    ['rimga', 'Rimga', 'linear-gradient(135deg,#123D2A,#D9B36C)'], ['glass', 'Glass', 'linear-gradient(135deg,#BFE3F0,#F5B26B,#E9A7C9)'],
    ['afis', 'Afiș', '#F6EFE9'], ['tabla', 'Tablă', '#2F4A3C'], ['avva', 'Universal Soft', '#8E2D3A'],
    ['custom', 'Custom', '#8A948F'], ['ai-generated', 'Alege pentru mine ✨', '']
  ];
  const strip = document.createElement('div');
  strip.className = 'ai-live-edit-toolbar__themes';
  strip.hidden = true;
  const curTheme = () => {
    const p = String(window.__aiTemplatePendingStyle || '').trim().toLowerCase();
    if (THEMES.some((t) => t[0] === p && p !== 'ai-generated')) return p;
    return String((getCurrentSettings() || {}).clientTemplateStyle || '').trim().toLowerCase();
  };
  const paintThemes = () => {
    const c = curTheme();
    strip.querySelectorAll('button').forEach((b) => b.classList.toggle('is-on', b.dataset.theme === c));
  };
  // temele vechi (fără toate secțiunile) apar doar contului care le folosește deja
  const LEGACY = new Set(['custom', 'avva', 'ai-generated']);
  const savedTheme = String((getCurrentSettings() || {}).clientTemplateStyle || '').trim().toLowerCase();
  THEMES.filter(([id]) => !LEGACY.has(id) || id === savedTheme).forEach(([id, label, sw]) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.dataset.theme = id;
    if (sw) {
      const i = document.createElement('i');
      i.style.background = sw;
      b.appendChild(i);
    }
    b.appendChild(document.createTextNode(label));
    b.addEventListener('click', async () => {
      strip.querySelectorAll('button').forEach((x) => x.classList.remove('is-on'));
      b.classList.add('is-on');
      try { await applyAiLayoutModel(id); } catch (_) { /* ignore */ }
      paintThemes();
    });
    strip.appendChild(b);
  });
  const setStrip = (open) => {
    aiThemeStripOpen = open;
    strip.hidden = !open;
    regenerateBtn.classList.toggle('is-on', open);
    regenerateBtn.setAttribute('aria-expanded', String(open));
    if (open) {
      const pal = bar.querySelector('.ai-live-edit-toolbar__palette');
      if (pal) pal.hidden = true;
      strip.style.top = `${regenerateBtn.offsetTop + regenerateBtn.offsetHeight / 2}px`;
      const r = bar.getBoundingClientRect();
      if (r.width) strip.style.width = `${Math.max(160, r.left - 8)}px`;
      paintThemes();
      const on = strip.querySelector('button.is-on');
      if (on) strip.scrollLeft = Math.max(0, on.offsetLeft - 18);
    }
  };
  regenerateBtn.addEventListener('click', () => setStrip(strip.hidden));

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.className = 'btn-primary ai-live-edit-toolbar__btn ai-live-edit-toolbar__btn--apply';
  saveBtn.id = 'aiLayoutApplyBtn';
  saveBtn.textContent = 'Aplică';
  saveBtn.title = 'Aplică modelul';
  saveBtn.setAttribute('aria-label', 'Aplică modelul');
  saveBtn.addEventListener('click', () => {
    applyAiLayoutSuggestion();
  });

  // Culoarea temei (pentru toate temele): bulina sus, paleta se deschide în stânga barei
  const ACC = [['gold', 'Auriu', '#D9B36C'], ['rose', 'Roz', '#FF7AA8'], ['blue', 'Albastru', '#6CB4FF'],
    ['emerald', 'Verde', '#5BE08F'], ['purple', 'Mov', '#C08BFF'], ['orange', 'Portocaliu', '#FF9F4A'], ['silver', 'Argintiu', '#D5DBE5']];
  const curAcc = () => String((getCurrentSettings() || {}).customTemplateAccent || '').trim().toLowerCase() || 'gold';
  const colorWrap = document.createElement('div');
  colorWrap.className = 'ai-live-edit-toolbar__color';
  const colorBtn = document.createElement('button');
  colorBtn.type = 'button';
  colorBtn.className = 'ai-live-edit-toolbar__colorbtn';
  colorBtn.title = 'Culoarea temei';
  colorBtn.setAttribute('aria-label', 'Culoarea temei');
  colorBtn.setAttribute('aria-expanded', 'false');
  const dot = document.createElement('span');
  colorBtn.appendChild(dot);
  const panel = document.createElement('div');
  panel.className = 'ai-live-edit-toolbar__palette';
  panel.hidden = true;
  const paint = () => {
    const c = curAcc();
    dot.style.background = (ACC.find((a) => a[0] === c) || ACC[0])[2];
    panel.querySelectorAll('button').forEach((b) => b.classList.toggle('is-on', b.dataset.acc === c));
  };
  ACC.forEach(([id, label, hex]) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.dataset.acc = id;
    b.title = label;
    b.setAttribute('aria-label', label);
    b.style.background = hex;
    b.addEventListener('click', async () => {
      await setCustomAccent(id);
      paint();
    });
    panel.appendChild(b);
  });
  colorBtn.addEventListener('click', () => {
    panel.hidden = !panel.hidden;
    if (!panel.hidden && !strip.hidden) setStrip(false);
    colorBtn.setAttribute('aria-expanded', String(!panel.hidden));
  });
  colorWrap.append(colorBtn, panel);
  paint();

  bar.appendChild(colorWrap);
  bar.appendChild(backBtn);
  bar.appendChild(regenerateBtn);
  bar.appendChild(saveBtn);
  bar.appendChild(strip);
  document.body.appendChild(bar);
  bar.style.display = '';
  // bara se redesenează după fiecare temă aleasă → banda rămâne deschisă
  if (aiThemeStripOpen) setStrip(true);
}

function bindAiLiveEditorIfNeeded() {
  if (aiLiveEditBound) return;
  aiLiveEditBound = true;
  document.addEventListener('pointerdown', (ev) => {
    if (!window.__aiLayoutPreviewActive) return;

    const heroHandle = ev.target?.closest?.('[data-ai-drag-hero]');
    if (heroHandle) {
      const dragHero = heroHandle.getAttribute('data-ai-drag-hero');
      if (dragHero && AI_HERO_SECTIONS.includes(dragHero)) {
        ev.preventDefault();
        const otherHero = dragHero === 'promo' ? 'duo' : 'promo';
        let lockApplied = false;
        const prevBodyOverflow = document.body.style.overflow;
        const prevTouchAction = document.body.style.touchAction;
        const clearHints = () => {
          document.querySelectorAll('[data-hero-section].ai-live-drop-before, [data-hero-section].ai-live-drop-after').forEach((el) => {
            el.classList.remove('ai-live-drop-before', 'ai-live-drop-after');
          });
        };
        const onMove = (e) => {
          if (!lockApplied) {
            lockApplied = true;
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
          }
          clearHints();
          const target = document.querySelector(`[data-hero-section="${otherHero}"]`);
          if (!target) return;
          const rect = target.getBoundingClientRect();
          const before = e.clientY < rect.top + rect.height / 2;
          target.classList.add(before ? 'ai-live-drop-before' : 'ai-live-drop-after');
        };
        const onUp = (e) => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          clearHints();
          document.body.style.overflow = prevBodyOverflow;
          document.body.style.touchAction = prevTouchAction;
          const target = document.querySelector(`[data-hero-section="${otherHero}"]`);
          if (!target || !aiLayoutPendingProposal) return;
          const rect = target.getBoundingClientRect();
          const before = e.clientY < rect.top + rect.height / 2;
          const nextTop = dragHero === 'promo' ? (before ? 'promo' : 'duo') : before ? 'duo' : 'promo';
          applyAiHeroTopSection(nextTop);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return;
      }
    }

    const handle = ev.target?.closest?.('[data-ai-drag-block]');
    if (!handle) return;
    const dragId = handle.getAttribute('data-ai-drag-block');
    if (!dragId || !AI_DRAGGABLE_BLOCKS.includes(dragId)) return;
    ev.preventDefault();

    let currentTarget = null;
    let before = true;
    let lockApplied = false;
    const prevBodyOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    const clearHints = () => {
      document.querySelectorAll('.ai-live-drop-before, .ai-live-drop-after').forEach((el) => {
        el.classList.remove('ai-live-drop-before', 'ai-live-drop-after');
      });
    };
    const applyScrollLock = () => {
      if (lockApplied) return;
      lockApplied = true;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    };
    const releaseScrollLock = () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
    const onMove = (e) => {
      applyScrollLock();
      clearHints();
      if (!aiLayoutPendingProposal) {
        currentTarget = null;
        return;
      }
      const orderedIds = (aiLayoutPendingProposal.order || [])
        .filter((id) => AI_DRAGGABLE_BLOCKS.includes(id) && id !== dragId);
      const positioned = orderedIds
        .map((id) => {
          const root = document.querySelector(`[data-layout-block="${id}"]`);
          if (!root || root.style.display === 'none') return null;
          const rect = root.getBoundingClientRect();
          return { id, rect, mid: rect.top + rect.height / 2 };
        })
        .filter(Boolean)
        .sort((a, b) => a.rect.top - b.rect.top);

      if (!positioned.length) {
        currentTarget = null;
        return;
      }

      const y = e.clientY;
      const found = positioned.find((p) => y < p.mid);
      if (found) {
        currentTarget = found.id;
        before = true;
      } else {
        currentTarget = positioned[positioned.length - 1].id;
        before = false;
      }
      const targetRoot = document.querySelector(`[data-layout-block="${currentTarget}"]`);
      if (targetRoot) targetRoot.classList.add(before ? 'ai-live-drop-before' : 'ai-live-drop-after');
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      clearHints();
      releaseScrollLock();
      if (!currentTarget || !aiLayoutPendingProposal) return;
      const arr = [...aiLayoutPendingProposal.order];
      const from = arr.indexOf(dragId);
      const to = arr.indexOf(currentTarget);
      if (from < 0 || to < 0) return;
      arr.splice(from, 1);
      const insertAt = before ? (from < to ? to - 1 : to) : (from < to ? to : to + 1);
      arr.splice(Math.max(0, Math.min(arr.length, insertAt)), 0, dragId);
      aiLayoutPendingProposal.order = arr;
      syncAiProposalHeroFromOrder();
      applyExternalLayoutProposal(aiLayoutPendingProposal);
      renderAiLayoutChanges(aiLayoutBaseConfig || getClientLayoutConfig(getCurrentSettings() || {}), aiLayoutPendingProposal);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  });
}

function renderAiLiveBlockControls() {
  if (!window.__aiLayoutPreviewActive) return;
  document.querySelectorAll('.ai-live-block-controls').forEach((el) => el.remove());
  renderAiLiveToolbar();
}

// Save admin settings (wrapper)
export async function saveAdminSettingsWrapper() {
  if (isSaving) return;

  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }

  const btn = $('saveAdminSettingsBtn');
  const textSpan = $('saveAdminSettingsText');
  const originalText = textSpan ? textSpan.textContent : 'Salvează';

  isSaving = true;
  try {
    await withSaveButtonState({
      btn,
      originalText,
      showSuccessSnackbar: true,
      successSnackbar: 'Setările au fost salvate',
      run: async () => {
        await saveAdminSettings(currentUser);
      }
    });
  } catch (_) {
    /* snackbar handled in guard */
  } finally {
    isSaving = false;
  }
}

let isSavingLocationHours = false;
let isSavingReservation = false;
let isSavingLocationMap = false;
let isSavingLocationLogo = false;
let isDeletingLocationLogo = false;

export async function saveLocationHoursSettingsWrapper() {
  if (isSavingLocationHours) return;

  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }

  const btn = $('saveLocationHoursBtn');
  const originalText = btn ? btn.textContent : 'Salvează';

  isSavingLocationHours = true;
  try {
    await withSaveButtonState({
      btn,
      originalText,
      showSuccessSnackbar: true,
      successSnackbar: 'Imagine program salvată cu succes.',
      run: async () => {
        await saveLocationHoursSettings({ quiet: true });
      }
    });
  } catch (_) {
    /* snackbar handled in guard */
  } finally {
    isSavingLocationHours = false;
  }
}

export async function saveLocationLogoSettingsWrapper() {
  if (isSavingLocationLogo) return;
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');
  const btn = $('saveLocationLogoBtn');
  const originalText = btn ? btn.textContent : 'Salvează';
  isSavingLocationLogo = true;
  try {
    await withSaveButtonState({
      btn,
      originalText,
      showSuccessSnackbar: true,
      successSnackbar: 'Logo și slogan salvate.',
      run: async () => {
        await saveLocationLogoSettings({ quiet: true });
      }
    });
  } catch (_) {
    /* snackbar handled in guard */
  } finally {
    isSavingLocationLogo = false;
  }
}

export async function deleteLocationLogoWrapper() {
  if (isDeletingLocationLogo) return;
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');
  const btn = $('deleteLocationLogoBtn');
  isDeletingLocationLogo = true;
  if (btn) btn.disabled = true;
  try {
    await deleteLocationLogo();
  } catch (e) {
    logger.error(e);
    showSnackbar(e?.message || 'Eroare la ștergere.', 'error');
  } finally {
    isDeletingLocationLogo = false;
    if (btn) btn.disabled = false;
  }
}

export async function saveLocationMapSettingsWrapper() {
  if (isSavingLocationMap) return;

  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }

  const btn = $('saveLocationMapBtn');
  const originalText = btn ? btn.textContent : 'Salvează';

  isSavingLocationMap = true;
  try {
    await withSaveButtonState({
      btn,
      originalText,
      showSuccessSnackbar: true,
      successSnackbar: 'Harta locației a fost salvată.',
      run: async () => {
        await saveLocationMapSettings({ quiet: true });
      }
    });
  } catch (_) {
    /* snackbar handled in guard */
  } finally {
    isSavingLocationMap = false;
  }
}

let isSavingEventModule = false;

export async function saveEventModuleSettingsWrapper() {
  if (isSavingEventModule) return;

  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }

  const btn = $('saveEventModuleBtn');
  const originalText = btn ? btn.textContent : 'Salvează';

  isSavingEventModule = true;
  try {
    await withSaveButtonState({
      btn,
      originalText,
      showSuccessSnackbar: true,
      successSnackbar: 'Modul eveniment salvat.',
      run: async () => {
        await saveEventModuleSettings({ quiet: true });
      }
    });
  } catch (_) {
    /* snackbar handled in guard */
  } finally {
    isSavingEventModule = false;
  }
}

let isSavingClientInterfaceModule = false;

export async function saveClientInterfaceModuleWrapper(moduleId) {
  if (isSavingClientInterfaceModule) {
    showSnackbar('Salvarea este deja în curs…', 'info');
    return;
  }

  const currentUser = getCurrentUser();
  if (!currentUser) {
    showSnackbar('Nu ești logat', 'error');
    return;
  }

  const btnArg = document.getElementById('clientInterfaceModuleSaveBtn')?.getAttribute('data-arg');
  const id = String(moduleId || btnArg || '').trim();
  if (!id) {
    showSnackbar('Modul necunoscut. Închide și redeschide configurarea.', 'error');
    return;
  }

  const btn = document.getElementById('clientInterfaceModuleSaveBtn');
  const originalText = btn ? btn.textContent : 'Salvează';

  isSavingClientInterfaceModule = true;
  try {
    await withSaveButtonState({
      btn,
      originalText,
      showSuccessSnackbar: true,
      successSnackbar: 'Setările au fost salvate.',
      run: async () => {
        await saveClientInterfaceModuleSettings(id);
      }
    });
    try {
      document.dispatchEvent(
        new CustomEvent('sj:client-interface-module-saved', { detail: { moduleId: id } })
      );
    } catch (_) {}
  } catch (_) {
    /* snackbar handled in guard */
  } finally {
    isSavingClientInterfaceModule = false;
  }
}

export async function saveReservationSettingsWrapper() {
  if (isSavingReservation) return;

  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }

  const btn = $('saveReservationBtn');
  const originalText = btn ? btn.textContent : 'Salvează';

  isSavingReservation = true;
  try {
    await withSaveButtonState({
      btn,
      originalText,
      showSuccessSnackbar: true,
      successSnackbar: 'Setările de contact au fost salvate.',
      run: async () => {
        await saveReservationSettings({ quiet: true });
      }
    });
  } catch (_) {
    /* snackbar handled in guard */
  } finally {
    isSavingReservation = false;
  }
}

// Reset tries (wrapper)
export async function resetTriesWrapper() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }
  
  try {
    await resetTriesBackend();
    // Reload settings to update UI
    const { loadSettings } = await import('./settings.js?v=83.58');
    await loadSettings(currentUser.uid, true);
  } catch (e) {
    logger.error('[resetTriesWrapper] Error:', e);
    showSnackbar('Eroare la resetarea încercărilor: ' + (e?.message || 'necunoscută'));
  }
}

// Export my data (GDPR)
export async function exportMyData() {
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');
  
  try {
    try {
      await loadScriptOnce('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js', 'XLSX', 8000);
    } catch (e) {
      logger.warn('[exportMyData] XLSX lazy-load failed:', e);
    }
    // Use Cloud Function instead of direct Firestore reads
    await exportUserDataBackend();
  } catch (e) {
    logger.error('[exportMyData] Error:', e);
    showSnackbar('Eroare la exportul datelor: ' + (e?.message || 'necunoscută'));
  }
}

// Delete account functions
export function openDeleteDialog() {
  const dialog = $('deleteDialog');
  if (dialog) dialog.classList.add('active');
}

export function closeDeleteDialog() {
  const dialog = $('deleteDialog');
  if (!dialog) return;
  
  dialog.classList.remove('active');
  const confirmText = $('deleteConfirmText');
  const confirmChk = $('deleteConfirmChk');
  const reauthWrap = $('reauthWrap');
  const reauthPass = $('reauthPass');
  
  if (confirmText) confirmText.value = '';
  if (confirmChk) confirmChk.checked = false;
  if (reauthWrap) reauthWrap.classList.add('hidden');
  if (reauthPass) reauthPass.value = '';
  
  setDeleteProgress(0);
}

function setDeleteProgress(p) {
  const bar = $('deleteProgressBar');
  if (bar) {
    bar.style.width = Math.max(0, Math.min(100, Math.round(p))) + '%';
  }
}

async function reauthIfNeeded(err, currentUser) {
  if (err && String(err.code || '').includes('requires-recent-login')) {
    const reauthWrap = $('reauthWrap');
    if (reauthWrap) reauthWrap.classList.remove('hidden');
    
    const pass = ($('reauthPass').value || '').trim();
    if (!pass) throw err;
    
    const cred = firebase.auth.EmailAuthProvider.credential(currentUser.email, pass);
    await currentUser.reauthenticateWithCredential(cred);
    return true;
  }
  return false;
}

async function deleteAllClaims(uid) {
  while (true) {
    const snap = await db.collection('claims').where('adminId', '==', uid).limit(250).get();
    if (snap.empty) break;
    const batch = db.batch();
    snap.forEach(doc => batch.delete(doc.ref));
    await batch.commit();
  }
}

async function deleteAllStorage(uid) {
  async function delFolder(prefix) {
    const ref = storage.ref(prefix);
    try {
      const res = await ref.listAll();
      for (const fileRef of res.items) {
        try {
          await fileRef.delete();
        } catch (e) {
          // Log individual file deletion errors but continue
          logger.warn('[deleteAllStorage] Error deleting file:', fileRef.fullPath, e);
        }
      }
      for (const dirRef of res.prefixes) {
        await delFolder(dirRef.fullPath);
      }
    } catch (e) {
      // Log folder deletion errors but continue
      logger.warn('[deleteAllStorage] Error deleting folder:', prefix, e);
    }
  }
  
  try {
    await delFolder(uid + '/');
    logger.log('[deleteAllStorage] Storage deletion completed for user:', uid);
  } catch (e) {
    // Storage deletion is best-effort - log but don't block account deletion
    logger.warn('[deleteAllStorage] Storage deletion incomplete (likely permission denied):', e);
  }
}

export async function deleteAccountNow() {
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');
  
  const confirmText = $('deleteConfirmText');
  const confirmChk = $('deleteConfirmChk');
  
  if ((confirmText?.value || '').trim() !== 'STERG' || !confirmChk?.checked) {
    showSnackbar('Tastează STERG și bifează confirmarea.');
    return;
  }

  try {
    setDeleteProgress(10);
    
    // Use Cloud Function - it handles everything (claims, settings, storage, auth)
    await deleteUserAccountBackend('STERG');
    
    setDeleteProgress(100);
    closeDeleteDialog();
    await auth.signOut();
    resetRegisterForm();
    resetAdminUI();
    showPage('loginPage');
  } catch (e) {
    logger.error('[deleteAccountNow] Error:', e);
    showSnackbar('Eroare ștergere: ' + (e?.message || 'necunoscută'));
    setDeleteProgress(0);
  }
}

// Tutorial
// Start campaign
export async function startCampaign(notary = null) {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }

  try {
    // Use Cloud Function instead of direct Firestore update
    await startCampaignBackend(notary);
    
    // ✅ FIX: Resetează offlinePlaysPending când pornești campania
    const { getCurrentSettings, setCurrentSettings } = await import('./settings.js?v=83.58');
    const currentSettings = getCurrentSettings();
    if (currentSettings) {
      currentSettings.offlinePlaysPending = 0;
      setCurrentSettings(currentSettings);
    }
    
    // Reload settings to update UI
    const { loadSettings } = await import('./settings.js?v=83.58');
    await loadSettings(currentUser.uid, true);
    
    // Update Mod Client card info
    const { updateSectionSummaries } = await import('../ui/admin-collapsible.js?v=83.58');
    updateSectionSummaries();
  } catch (e) {
    logger.error('[startCampaign] Error:', e);
    showSnackbar('Eroare la pornirea campaniei: ' + (e?.message || 'necunoscută'));
    throw e; // Re-throw pentru a permite handler-ului să gestioneze eroarea
  }
}

export function openStartCampaignModal() {
  const modal = document.getElementById('startCampaignModal');
  if (modal) {
    // ✅ FIX: Reset button state when opening modal
    const btn = $('confirmStartCampaignBtn');
    const cancelBtn = $('cancelStartCampaignBtn');
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = 'Pornește';
    }
    if (cancelBtn) {
      cancelBtn.disabled = false;
      cancelBtn.style.pointerEvents = '';
      cancelBtn.classList.remove('btn-disabled-state');
    }
    // Reset flag in case it was stuck
    isStarting = false;
    const chk = $('sjNotaryConfirm');
    if (chk) chk.checked = false;
    const na = getCurrentSettings()?.notaryAct || {};
    const kindNow = getCurrentSettings()?.campaignKind;
    const isScratchKind = kindNow === 'scratch';
    const guaranteed = kindNow === 'guaranteed' || isScratchKind;
    const nbox = $('sjNotaryBox');
    if (nbox) nbox.hidden = guaranteed;
    const h2 = modal.querySelector('h2');
    if (h2) h2.textContent = isScratchKind ? 'Pornește „Răzuiește și câștigă”' : guaranteed ? 'Pornește campania instant' : getCurrentSettings()?.campaignKind === 'instant' ? 'Pornește campania aleatorie' : 'Pornește campania cu extragere';
    const sub = h2 && h2.nextElementSibling;
    if (sub && sub.tagName === 'P') sub.textContent = isScratchKind
      ? 'Biletele se amestecă acum și se blochează. Clienții joacă doar cu numărul bonului: un bon, un bilet.'
      : guaranteed
      ? 'Fiecare client care scanează primește cadoul. Fără notar, regulamentul simplu e generat automat.'
      : 'Clienții joacă și pot câștiga premiile. Înainte de pornire, regulamentul trebuie autentificat la notar.';
    if ($('sjNotaryNo')) $('sjNotaryNo').value = '';
    if ($('sjNotaryDate')) $('sjNotaryDate').value = '';
    if ($('sjNotaryOffice')) $('sjNotaryOffice').value = na.office || '';
    // Varianta A (SpressJoy organizator): act notarial încărcat, text identic/modificat, blocare la pornire
    import('../legal/legal-notary.js?v=83.58').then((m) => { m.mountNotaryExtras(getCurrentSettings() || {}); m.mountOwnRules(getCurrentSettings() || {}); }).catch(() => {});
    
    modal.classList.add('active');
  }
}

export function closeStartCampaignModal() {
  const modal = document.getElementById('startCampaignModal');
  if (modal) {
    modal.classList.remove('active');
    
    // ✅ FIX: Reset button state when closing modal
    const btn = $('confirmStartCampaignBtn');
    const cancelBtn = $('cancelStartCampaignBtn');
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = 'Pornește';
    }
    if (cancelBtn) {
      cancelBtn.disabled = false;
      cancelBtn.style.pointerEvents = '';
      cancelBtn.classList.remove('btn-disabled-state');
    }
    // Reset flag when closing
    isStarting = false;
  }
}

export async function confirmStartCampaign() {
  // Protecție anti-dublu-click
  if (isStarting) return;
  // Campania la noroc = loterie publicitară → confirmarea autentificării notariale
  const isGuaranteed = ['guaranteed', 'scratch'].includes(getCurrentSettings()?.campaignKind);
  const notaryOk = isGuaranteed || $('sjNotaryConfirm')?.checked === true;
  if (!notaryOk) {
    showSnackbar('Bifează confirmarea că regulamentul a fost autentificat la notar.', 'error');
    $('sjNotaryConfirm')?.focus();
    return;
  }
  const notary = isGuaranteed ? null : {
    confirmed: true,
    number: String($('sjNotaryNo')?.value || '').trim().slice(0, 40),
    date: String($('sjNotaryDate')?.value || '').trim(),
    office: String($('sjNotaryOffice')?.value || '').trim().slice(0, 100)
  };
  // Varianta A: documentul autentificat se încarcă și regulamentul + informarea se blochează
  if (notary) {
    try {
      const lg = await import('../legal/legal-notary.js?v=83.58');
      const cur = getCurrentSettings() || {};
      if (lg.isVariantA(cur)) {
        const extra = await lg.collectNotaryLegal(cur, notary);
        notary.legal = extra.legal;
      } else if (lg.getOwnRules()) {
        notary.ownRules = lg.getOwnRules();
      }
    } catch (e) {
      showSnackbar(e?.message || 'Nu am putut pregăti regulamentul pentru blocare.', 'error');
      return;
    }
  }
  
  const btn = $('confirmStartCampaignBtn');
  const cancelBtn = $('cancelStartCampaignBtn');
  const originalText = btn ? btn.textContent : 'Pornește';
  
  // Setează flag și dezactivează ambele butoane
  isStarting = true;
  if (btn) {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.classList.add('btn-disabled-state');
    btn.innerHTML = '<span class="inline-spinner"></span> Se pornește...';
  }
  if (cancelBtn) {
    cancelBtn.disabled = true;
    cancelBtn.style.pointerEvents = 'none';
    cancelBtn.classList.add('btn-disabled-state');
  }
  
  try {
    const { withModClientModuleToggleBusy } = await import('../ui/mod-client-modules.js?v=83.58');
    await withModClientModuleToggleBusy('campanii', () => startCampaign(notary));
    // La succes: afișează mesaj de confirmare și închide modalul
    showSnackbar('Campania a pornit');
    closeStartCampaignModal();
  } catch (e) {
    // La eroare: păstrează modalul deschis și reactivează ambele butoane
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    if (cancelBtn) {
      cancelBtn.disabled = false;
      cancelBtn.style.pointerEvents = '';
      cancelBtn.classList.remove('btn-disabled-state');
    }
    // Mesajul de eroare este afișat de startCampaign
  } finally {
    // Resetează flag-ul
    isStarting = false;
  }
}

// Stop campaign
export async function stopCampaign() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }

  try {
    // Use Cloud Function instead of direct Firestore writes
    // The Cloud Function handles all archiving, claims copying, and status updates
    await stopCampaignBackend();
    
    // ✅ FIX: Resetează offlinePlaysPending când oprești campania
    const { getCurrentSettings, setCurrentSettings } = await import('./settings.js?v=83.58');
    const currentSettings = getCurrentSettings();
    if (currentSettings) {
      currentSettings.offlinePlaysPending = 0;
      setCurrentSettings(currentSettings);
    }
    
    // Reload settings to update UI
    const { loadSettings } = await import('./settings.js?v=83.58');
    await loadSettings(currentUser.uid, true);
    
    // Update Mod Client card info and refresh archived campaigns
    const { updateSectionSummaries } = await import('../ui/admin-collapsible.js?v=83.58');
    updateSectionSummaries();
    
    // Refresh archived campaigns list if on dashboard
    // ✅ FIX: Reload archived campaigns list doar dacă modalul este deschis
    const modal = document.getElementById('closedCampaignsModal');
    const container = document.getElementById('closedCampaignsModalList');
    
    if (modal && container && modal.classList.contains('active')) {
      // Modalul este deschis, reîncarcă lista în modal
      if (typeof window.loadArchivedCampaigns === 'function') {
        window.loadArchivedCampaigns(true);
      }
    } else {
      // Modalul nu este deschis, doar actualizează badge-ul
      if (window.updateClosedCampaignsBadge) {
        window.updateClosedCampaignsBadge();
      }
    }
  } catch (e) {
    logger.error('[stopCampaign] Error:', e);
    showSnackbar('Eroare la oprirea campaniei: ' + (e?.message || 'necunoscută'));
    throw e; // Re-throw pentru a permite handler-ului să gestioneze eroarea
  }
}

export function openStopCampaignModal() {
  const modal = document.getElementById('stopCampaignModal');
  if (modal) {
    // ✅ FIX: Reset button state when opening modal
    const btn = $('confirmStopCampaignBtn');
    const cancelBtn = $('cancelStopCampaignBtn');
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = 'Da, oprește campania';
    }
    if (cancelBtn) {
      cancelBtn.disabled = false;
      cancelBtn.style.pointerEvents = '';
      cancelBtn.classList.remove('btn-disabled-state');
    }
    // Reset flag in case it was stuck
    isStopping = false;
    
    modal.classList.add('active');
  }
}

export function closeStopCampaignModal() {
  const modal = document.getElementById('stopCampaignModal');
  if (modal) {
    modal.classList.remove('active');
    
    // ✅ FIX: Reset button state when closing modal
    const btn = $('confirmStopCampaignBtn');
    const cancelBtn = $('cancelStopCampaignBtn');
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = 'Da, oprește campania';
    }
    if (cancelBtn) {
      cancelBtn.disabled = false;
      cancelBtn.style.pointerEvents = '';
      cancelBtn.classList.remove('btn-disabled-state');
    }
    // Reset flag when closing
    isStopping = false;
  }
}

export async function confirmStopCampaign() {
  // Protecție anti-dublu-click
  if (isStopping) return;
  
  const btn = $('confirmStopCampaignBtn');
  const cancelBtn = $('cancelStopCampaignBtn');
  const originalText = btn ? btn.textContent : 'Da, oprește campania';
  
  // Setează flag și dezactivează ambele butoane
  isStopping = true;
  if (btn) {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.classList.add('btn-disabled-state');
    btn.innerHTML = '<span class="inline-spinner"></span> Se oprește...';
  }
  if (cancelBtn) {
    cancelBtn.disabled = true;
    cancelBtn.style.pointerEvents = 'none';
    cancelBtn.classList.add('btn-disabled-state');
  }
  
  try {
    const { withModClientModuleToggleBusy } = await import('../ui/mod-client-modules.js?v=83.58');
    await withModClientModuleToggleBusy('campanii', () => stopCampaign());
    // La succes: afișează mesaj de confirmare și închide modalul
    showSnackbar('Campania s-a oprit');
    closeStopCampaignModal();
  } catch (e) {
    // La eroare: păstrează modalul deschis și reactivează ambele butoane
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    if (cancelBtn) {
      cancelBtn.disabled = false;
      cancelBtn.style.pointerEvents = '';
      cancelBtn.classList.remove('btn-disabled-state');
    }
    // Mesajul de eroare este afișat de stopCampaign
  } finally {
    // Resetează flag-ul
    isStopping = false;
  }
}

/** Ieșire din preview AI — restaurare vizuală fără salvare. */
export function exitAiLayoutPreview() {
  aiThemeStripOpen = false;
  window.__sjOwnSlug = null;
  document.body.classList.remove('sj-joy-prev');
  setAiClientPreviewMode(false);
}

export function enterAiLayoutPreview() {
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');

  const currentSettings = getCurrentSettings() || {};
  // în previzualizare, campania/fidelizarea merg pe linkul propriu, în mod test (nu se contorizează)
  window.__sjOwnSlug = String(currentSettings.publicSlug || '').trim() || null;
  import('./backend.js?v=83.58').then((m) => m.warmFunctions(['submitPublicClaim', 'redeemPublicVoucher'])).catch(() => {});
  aiTemplateBaseStyle = getClientTemplateStyleFromSettings(currentSettings);
  aiTemplateBaseAiVariant = currentSettings.clientTemplateAiVariant || null;
  aiTemplatePendingStyle = aiTemplateBaseStyle;
  aiTemplatePendingAiVariant = aiTemplateBaseAiVariant;
  syncAiTemplatePendingStyleWindow();

  applyClientTemplateToPage(aiTemplatePendingStyle, {
    aiVariant: aiTemplatePendingAiVariant,
    settings: currentSettings
  });
  syncClientTemplateHeader(currentSettings);
  setAiClientPreviewMode(true);
  showPage('clientPage');
  renderAiLiveBlockControls();
  refreshClientPreviewUi();
  setTimeout(() => renderAiLiveBlockControls(), 120);
  showSnackbar('Așa o văd clienții. Alege o temă din „Teme” și apasă „Aplică”.', 'info');
}

export async function setCustomAccent(accent) {
  const ACCENTS = ['gold', 'rose', 'blue', 'emerald', 'silver', 'purple', 'orange'];
  const value = String(accent || 'gold').trim().toLowerCase();
  if (!ACCENTS.includes(value)) return;
  const page = document.getElementById('clientPage');
  if (page) {
    if (value === 'gold') delete page.dataset.accent;
    else page.dataset.accent = value;
  }
  document.querySelectorAll('.custom-accent-swatch').forEach((btn) => {
    const isActive = btn.dataset.arg === value;
    btn.classList.toggle('is-active', isActive);
    btn.setAttribute('aria-pressed', String(isActive));
  });
  const currentUser = getCurrentUser();
  if (currentUser) {
    const cs = getCurrentSettings() || {};
    const patch = { customTemplateAccent: value === 'gold' ? '' : value };
    setCurrentSettings({ ...cs, ...patch });
    // Salvare reală pe server (înainte apela o funcție care nu există → culoarea nu rămânea)
    saveCampaignSettingsBackend(patch)
      .then(() => showSnackbar('Culoarea a fost salvată.', 'success'))
      .catch((e) => showSnackbar(e?.message || 'Nu am putut salva culoarea.', 'error'));
  }
}

export function openAiLayoutModelPicker() {
  if (!window.__aiLayoutPreviewActive) {
    showSnackbar('Intră mai întâi în preview cu „Optimizează cu AI”.', 'info');
    return;
  }
  const customCard = document.getElementById('aiLayoutModelCustom');
  if (customCard) customCard.style.display = '';
  const avvaCard = document.getElementById('aiLayoutModelAvva');
  if (avvaCard) avvaCard.style.display = '';
  const popup = $('aiLayoutModelPicker');
  if (popup) popup.classList.add('active');
}

export function closeAiLayoutModelPicker() {
  const popup = $('aiLayoutModelPicker');
  if (popup) popup.classList.remove('active');
}

export async function applyAiLayoutModel(modelId) {
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');
  if (!window.__aiLayoutPreviewActive) {
    showSnackbar('Intră mai întâi în preview cu „Optimizează cu AI”.', 'info');
    return;
  }

  const normalizedId = String(modelId || '').trim().toLowerCase();
  closeAiLayoutModelPicker();

  if (normalizedId === 'standard') {
    aiTemplatePendingStyle = 'standard';
    syncAiTemplatePendingStyleWindow();
    aiTemplatePendingAiVariant = null;
    aiLayoutPendingProposal = null;
    const settings = getCurrentSettings() || {};
    await applyClientTemplateToPage('standard', { settings });
    applyStandardLayoutDefaults();
    refreshClientPreviewUi();
    setTimeout(() => renderAiLiveBlockControls(), 80);
    showSnackbar('Standard aplicat în preview. Apasă „Aplică” dacă îți place.', 'info');
    return;
  }

  if (normalizedId === 'ai-generated') {
    // „Alege pentru mine”: AI-ul alege una dintre temele rămase (apăsat din nou → altă temă)
    const POOL = ['cloud', 'cloud-light', 'avva'];
    let picked = '';
    try {
      const currentSettings = getCurrentSettings() || {};
      const response = await suggestClientLayoutAIBackend({
        businessType: String(currentSettings.businessType || currentSettings.companyName || '').trim(),
        goal: 'Alege tema potrivită pentru acest local',
        templatePick: true,
        currentLayoutConfig: getClientLayoutConfig(currentSettings),
        suppressErrorSnackbar: true
      });
      picked = String(response?.templateStyle || '').trim().toLowerCase();
    } catch (_) {
      picked = '';
    }
    if (!POOL.includes(picked) || picked === lastAiTemplatePick) {
      const rest = POOL.filter((id) => id !== lastAiTemplatePick);
      picked = rest[Math.floor(Math.random() * rest.length)] || POOL[0];
    }
    lastAiTemplatePick = picked;
    return applyAiLayoutModel(picked);
  }

  if (['custom', 'avva', 'afis', 'tabla', 'glass', 'cloud', 'cloud-light', 'rimga'].includes(normalizedId)) {
    aiTemplatePendingStyle = normalizedId;
    syncAiTemplatePendingStyleWindow();
    aiTemplatePendingAiVariant = null;
    const settings = getCurrentSettings() || {};
    await applyClientTemplateToPage(normalizedId, { settings });
    syncClientTemplateHeader(settings);
    if (normalizedId === 'custom' || normalizedId === 'cloud' || normalizedId === 'cloud-light' || normalizedId === 'rimga') {
      import('../ui/client-custom-template.js?v=83.58')
        .then((m) => m.syncCustomTemplate?.(settings))
        .catch(() => {});
    }
    refreshClientPreviewUi();
    setTimeout(() => renderAiLiveBlockControls(), 80);
    const label = CLIENT_TEMPLATE_LABELS[normalizedId] || normalizedId;
    showSnackbar(`${label} aplicat în preview. Apasă „Aplică modelul” dacă îți place.`, 'success');
    return;
  }

  showSnackbar('Stil necunoscut. Alege un obiectiv din listă sau „Alege pentru mine”.', 'warning');
}

export function openAiLayoutModal() {
  aiLayoutPendingProposal = null;
  aiSettingsPatchPending = null;
  aiLayoutBaseConfig = null;
  resetAiLayoutChanges();
  resetAiManualEditor();
  setAiClientPreviewMode(false);
  const chat = $('aiLayoutChatMessages');
  if (chat) chat.replaceChildren();
  const companyName = String((getCurrentSettings() || {}).companyName || '').trim() || 'prietene';
  appendAiChatLine(`Buna, ${companyName}! Sunt asistentul tau de layout.`);
  appendAiChatLine('Cu ce te pot ajuta azi?');
  const popup = $('aiLayoutPopup');
  if (popup) popup.classList.add('active');
  const applyBtn = $('aiLayoutApplyBtn');
  const previewBtn = $('aiLayoutPreviewBtn');
  if (applyBtn) applyBtn.style.display = 'none';
  if (previewBtn) previewBtn.style.display = 'none';
}

export function closeAiLayoutModal() {
  const popup = $('aiLayoutPopup');
  if (popup) popup.classList.remove('active');
}

export function sendAiChatPrompt() {
  const input = $('aiChatPromptInput');
  const prompt = String(input?.value || '').trim();
  if (!prompt) return;
  appendAiChatLine(`Tu: ${prompt}`);
  appendAiChatLine('AI: Generez o propunere pe baza mesajului tau...');
  if (input) input.value = '';
  generateAiLayoutSuggestion({ businessType: '', goal: prompt, preferredTone: '' });
}

export async function generateAiLayoutSuggestion(presetPayload = null) {
  if (aiLayoutGenerating) return;

  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');

  const payloadFromArgs = presetPayload && typeof presetPayload === 'object' ? presetPayload : null;
  const businessType = String(payloadFromArgs?.businessType || '').trim();
  const goal = String(payloadFromArgs?.goal || '').trim();
  const preferredTone = String(payloadFromArgs?.preferredTone || '').trim();
  const generateBtn = $('aiLayoutGenerateBtn');
  const applyBtn = $('aiLayoutApplyBtn');
  const previewBtn = $('aiLayoutPreviewBtn');
  const originalText = generateBtn?.textContent || 'Generează propunere';

  aiLayoutGenerating = true;
  if (generateBtn) {
    generateBtn.disabled = true;
    generateBtn.innerHTML = '<span class="inline-spinner"></span> Generez...';
  }

  try {
    const currentSettings = getCurrentSettings() || {};
    const currentLayout = getClientLayoutConfig(currentSettings);
    aiLayoutBaseConfig = JSON.parse(JSON.stringify(currentLayout));
    const response = await suggestClientLayoutAIBackend({
      businessType,
      goal,
      preferredTone,
      currentLayoutConfig: currentLayout
    });
    aiLayoutPendingProposal = response?.proposal || null;
    aiSettingsPatchPending = response?.settingsPatch && typeof response.settingsPatch === 'object'
      ? response.settingsPatch
      : null;
    if (!aiLayoutPendingProposal) {
      throw new Error('AI nu a returnat o propunere validă.');
    }

    const diffLines = formatAiDiffLines(currentLayout, aiLayoutPendingProposal);
    renderAiLayoutChanges(currentLayout, aiLayoutPendingProposal);
    appendAiChatLine(`AI: ${String(response?.intentSummary || '').trim() || `Am pregatit propunerea. ${diffLines[0] || 'Poti verifica modificarile si intra in preview.'}`}`);
    if (applyBtn) applyBtn.style.display = '';
    if (previewBtn) previewBtn.style.display = '';
    if (response?.usedFallback === true) {
      showSnackbar('AI a returnat un fallback safe. Încearcă o cerere mai specifică.', 'warning');
    } else if (diffLines.length === 1 && diffLines[0].startsWith('Nu există diferențe') && !aiSettingsPatchPending) {
      showSnackbar('AI nu a propus modificări reale pentru această cerere.', 'info');
    } else {
      showSnackbar('Propunere generată. Poți apăsa "Vezi preview AI" sau "Aplică propunerea".', 'success');
    }
  } catch (e) {
    showSnackbar('Nu am putut genera propunerea AI.', 'error');
  } finally {
    if (generateBtn) {
      generateBtn.disabled = false;
      generateBtn.textContent = originalText;
    }
    aiLayoutGenerating = false;
  }
}

export function previewAiLayoutSuggestion() {
  if (!aiLayoutPendingProposal) {
    const currentLayout = getClientLayoutConfig(getCurrentSettings() || {});
    aiLayoutPendingProposal = JSON.parse(JSON.stringify(currentLayout));
    aiLayoutBaseConfig = JSON.parse(JSON.stringify(currentLayout));
    appendAiChatLine('AI: Am intrat direct în preview pe layout-ul curent.');
  }
  hydrateAiLiveOffsetsFromProposal();
  applyExternalLayoutProposal(aiLayoutPendingProposal);
  setAiClientPreviewMode(true);
  applyAiLiveOffsets();
  closeAiLayoutModal();
  showPage('clientPage');
  setTimeout(() => {
    renderAiLiveBlockControls();
  }, 80);
  showSnackbar('Preview AI activ (fără joc). Apasă "Mod" pentru a reveni în Admin fără PIN.', 'info');
}

export async function applyAiLayoutSuggestion() {
  if (aiLayoutApplying) return;
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');

  const applyBtn = $('aiLayoutApplyBtn');
  const originalText = applyBtn?.textContent || 'Aplică modelul';
  aiLayoutApplying = true;
  if (applyBtn) {
    applyBtn.disabled = true;
    applyBtn.innerHTML = '<span class="inline-spinner"></span> Aplic modelul...';
  }

  try {
    showLoadingOverlay('Aplic stilul...');
    const currentSettings = getCurrentSettings() || {};
    if (aiSettingsPatchPending) {
      applyAiSettingsPatchToForm(aiSettingsPatchPending);
    }
    const patchFields = aiSettingsPatchPending && typeof aiSettingsPatchPending === 'object'
      ? aiSettingsPatchPending
      : {};
    const isStandard = aiTemplatePendingStyle === 'standard';
    const layoutConfig = isStandard
      ? cloneDefaultClientLayoutConfig()
      : aiLayoutPendingProposal
        ? aiLayoutPendingProposal
        : currentSettings.clientLayoutConfig;
    const templatePatch = {
      ...patchFields,
      clientTemplateStyle: aiTemplatePendingStyle,
      clientTemplateAiVariant:
        aiTemplatePendingStyle === 'ai-generated' ? aiTemplatePendingAiVariant : null,
      clientLayoutConfig: layoutConfig,
      ...(aiTemplatePendingStyle === 'custom'
        ? {
            customTemplateAccent:
              String(currentSettings.customTemplateAccent || '').trim() || null
          }
        : {})
    };
    setCurrentSettings({ ...currentSettings, ...templatePatch });

    await persistClientTemplateChoice(currentUser.uid, templatePatch);
    await loadSettings(currentUser.uid, true);

    const savedSettings = {
      ...(getCurrentSettings() || {}),
      clientTemplateStyle: templatePatch.clientTemplateStyle,
      clientTemplateAiVariant: templatePatch.clientTemplateAiVariant,
      clientLayoutConfig: templatePatch.clientLayoutConfig
    };
    setCurrentSettings(savedSettings);
    setAiClientPreviewMode(false);
    applyClientTemplateToPage(getClientTemplateStyleFromSettings(savedSettings), {
      aiVariant: savedSettings.clientTemplateAiVariant || null,
      settings: savedSettings
    });
    syncClientTemplateHeader(savedSettings);
    await refreshClientPreviewUi();

    closeAiLayoutModal();
    showPage('adminPage');
    const label = CLIENT_TEMPLATE_LABELS[aiTemplatePendingStyle] || aiTemplatePendingStyle;
    showSnackbar(`Stilul „${label}” a fost aplicat și salvat.`, 'success');
  } catch (e) {
    logger.warn('[applyAiLayoutSuggestion] save failed:', e);
    const msg =
      String(e?.message || '').trim() ||
      'Nu am putut salva stilul. Încearcă din nou.';
    showSnackbar(msg, 'error');
  } finally {
    hideLoadingOverlay();
    if (applyBtn) {
      applyBtn.disabled = false;
      applyBtn.textContent = originalText;
    }
    aiLayoutApplying = false;
  }
}

export function openTutorial() {
  const popup = $('tutorialPopup');
  if (!popup) return;
  popup.classList.add('active');
  const v = $('tutorialVideo');
  if (v) {
    try {
      v.currentTime = 0;
      v.play().catch((e) => {
        // Video autoplay may be blocked by browser - this is expected
        logger.warn('[openTutorial] Video autoplay blocked:', e);
      });
    } catch (e) {
      logger.error('[openTutorial] Error resetting video:', e);
    }
  }
}

export function closeTutorial() {
  const popup = $('tutorialPopup');
  if (!popup) return;
  popup.classList.remove('active');
  const v = $('tutorialVideo');
  if (v) {
    try {
      v.pause();
    } catch (e) {
      logger.error('[closeTutorial] Error pausing video:', e);
    }
  }
}

// Make available globally for onclick handlers
window.saveAdminSettings = saveAdminSettingsWrapper;
window.resetTries = resetTriesWrapper;
window.exportMyData = exportMyData;
window.openDeleteDialog = openDeleteDialog;
window.closeDeleteDialog = closeDeleteDialog;
window.deleteAccountNow = deleteAccountNow;
window.openTutorial = openTutorial;
window.closeTutorial = closeTutorial;
window.openStartCampaignModal = openStartCampaignModal;
window.closeStartCampaignModal = closeStartCampaignModal;
window.confirmStartCampaign = confirmStartCampaign;
window.openStopCampaignModal = openStopCampaignModal;
window.closeStopCampaignModal = closeStopCampaignModal;
window.confirmStopCampaign = confirmStopCampaign;
window.openAiLayoutModal = openAiLayoutModal;
window.closeAiLayoutModal = closeAiLayoutModal;
window.sendAiChatPrompt = sendAiChatPrompt;
window.generateAiLayoutSuggestion = generateAiLayoutSuggestion;
window.previewAiLayoutSuggestion = previewAiLayoutSuggestion;
window.applyAiLayoutSuggestion = applyAiLayoutSuggestion;
